import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { CreditCard, Calendar, Lock } from "lucide-react";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";

export default function CardActivation() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    cardNumber: "",
    expiryDate: "",
    cvv: "",
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    // Format card number with spaces
    if (field === "cardNumber") {
      value = value
        .replace(/\s/g, "")
        .replace(/(\d{4})/g, "$1 ")
        .trim();
      if (value.length > 19) return;
    }

    // Format expiry date
    if (field === "expiryDate") {
      value = value.replace(/\D/g, "");
      if (value.length >= 3) {
        value = value.slice(0, 2) + "/" + value.slice(2, 4);
      }
      if (value.length > 5) return;
    }

    // Limit CVV length
    if (field === "cvv") {
      value = value.replace(/\D/g, "");
      if (value.length > 3) return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (
      !formData.cardNumber ||
      formData.cardNumber.replace(/\s/g, "").length !== 16
    ) {
      newErrors.cardNumber = "Please enter a valid 16-digit card number";
    }

    if (!formData.expiryDate || formData.expiryDate.length !== 5) {
      newErrors.expiryDate = "Please enter a valid expiry date (MM/YY)";
    }

    if (!formData.cvv || formData.cvv.length !== 3) {
      newErrors.cvv = "Please enter a valid 3-digit CVV";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      // Pass card data to PIN setup page
      navigate("/card-pin-setup", {
        state: {
          cardData: {
            lastFour: formData.cardNumber.slice(-4),
            ...formData,
          },
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/mycard")} />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Title */}
        <div className="mt-6 mb-6">
          <h1 className="text-2xl font-bold">Activate Your Card</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Please enter the information below to activate your card.
          </p>
        </div>

        {/* Card Illustration */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-64 h-40 bg-gradient-to-br from-primary to-primary/80 rounded-xl shadow-lg flex items-center justify-center">
              <CreditCard className="w-16 h-16 text-white/50" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-64 h-40 bg-primary/20 rounded-xl -z-10" />
          </div>
        </div>

        {/* Form */}
        <Card className="bg-card/80">
          <CardContent className="p-6 space-y-4">
            {/* Card Number */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-muted-foreground" />
                Card Number
              </label>
              <Input
                type="text"
                placeholder="0000 0000 0000 0000"
                value={formData.cardNumber}
                onChange={(e) =>
                  handleInputChange("cardNumber", e.target.value)
                }
                className={`h-12 text-lg text-center ${
                  errors.cardNumber ? "border-red-500" : ""
                }`}
              />
              {errors.cardNumber && (
                <p className="text-xs text-red-500">{errors.cardNumber}</p>
              )}
            </div>

            {/* Expiry Date and CVV */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  Expiry Date
                </label>
                <Input
                  type="text"
                  placeholder="MM/YY"
                  value={formData.expiryDate}
                  onChange={(e) =>
                    handleInputChange("expiryDate", e.target.value)
                  }
                  className={`h-12 text-lg text-center ${
                    errors.expiryDate ? "border-red-500" : ""
                  }`}
                />
                {errors.expiryDate && (
                  <p className="text-xs text-red-500">{errors.expiryDate}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  CVV
                </label>
                <Input
                  type="text"
                  placeholder="000"
                  value={formData.cvv}
                  onChange={(e) => handleInputChange("cvv", e.target.value)}
                  className={`h-12 text-lg text-center ${
                    errors.cvv ? "border-red-500" : ""
                  }`}
                  maxLength="3"
                />
                {errors.cvv && (
                  <p className="text-xs text-red-500">{errors.cvv}</p>
                )}
              </div>
            </div>

            {/* Security Notice */}
            <div className="p-3 bg-blue-200/20 rounded-lg">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Your card information is encrypted and secure. We never store
                your CVV.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Next Button */}
        <div className="mt-6">
          <Button onClick={handleNext} className="w-full h-12">
            Next
          </Button>
        </div>
      </div>

      <BottomBar />
    </div>
  );
}
